<script lang="ts">
    import { getCachedEntry } from "../api"
    import { spaBack, spaNavigate } from "../lib/actions"
    import NotFound from "./NotFound.svelte"
    import ThumbnailSlides from "../lib/components/ThumbnailSlides.svelte"
    import LinkList from "../lib/components/LinkList.svelte"
    import { add2Cart } from "../lib/components/CartButton.svelte"
    import ProductConfigurator from "../lib/components/ProductConfigurator.svelte"
    import { cloneDeep } from "lodash"

    export let location: LocationStore = undefined
    export let id: string = undefined

    let openTypeFilter = false
    let openMore = false

    let loading = true
    let product: ProductEntry
    async function loadProduct() {
        loading = true
        product = null
        try {
            product = await getCachedEntry(
                "product",
                location ? { slug: location.path?.replace(/^\/products\//, "") } : { _id: id },
                { si: "2" }
            )
        } catch (e) {
            console.error(e)
        }
        loading = false
    }

    // only load new content if location.path or id changes
    let _location: typeof location
    let _id: typeof id
    $: if (location?.path != _location?.path || id != _id) {
        _id = id
        _location = location
        loadProduct()
    }

    function backToList() {
        if (location?.previousLocation?.path == "/products") {
            spaBack()
        } else {
            spaNavigate("/products")
        }
    }

    let configuratorScrollTargetContainer: HTMLDivElement
    let lastAnchor: string
    $: if (typeof window !== "undefined") {
        const targetAnchor = location.hash
        if (targetAnchor !== lastAnchor && configuratorScrollTargetContainer) {
            lastAnchor = targetAnchor
            if (targetAnchor == "#configurator") {
                setTimeout(() => {
                    configuratorScrollTargetContainer.scrollIntoView({
                        behavior: "instant",
                        block: "start",
                        inline: "nearest",
                    })
                    setTimeout(() => {
                        configuratorScrollTargetContainer.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                        })
                    }, 600) // after in-view scroll animation
                }, 10)
            }
        }
    }

    let productConfig: ArticleConfig
    let bomItemConfigs: ArticleConfig[]
</script>

<svelte:head>
    {#if product}
        {#if product.meta?.title || product.title}
            <title>SPORTident - {product.meta?.title || product.title}</title>
        {/if}
        {#if product.meta?.description}
            <meta name="description" content="{product.meta.description}" />
        {/if}
        {#if product.meta?.keywords}
            <meta name="keywords" content="{product.meta.keywords}" />
        {/if}
        {#if !product.active || (product.releaseDate && new Date(product.releaseDate) > new Date())}
            <meta name="robots" content="noindex" />
        {/if}
    {/if}
</svelte:head>

{#if loading}
    lade...
{:else if product}
    <ThumbnailSlides slides="{product?.images?.map((i) => ({ backgroundImage: i }))}" />

    <section class="products overflow-show">
        <div class="container-fluid">
            <div class="container">
                <div class="grid align-start">
                    <div class="col-md-3 gray-gradient-soft col-grid gap-s filter-col vp-xl vp-sm-xl">
                        <div class="badge col-12 tp-s bp-s">
                            {#if product.categories?.length}
                                {#each product.categories as categoryIdent}
                                    <span>{categoryIdent}</span>
                                {/each}
                            {/if}
                            {#if product.number}
                                <span>Art.-No. {product.number}</span>
                            {/if}
                        </div>
                        {#if product.lines?.length}
                            <div class="hidden-sm-down col-12 filters-wrapper">
                                <button
                                    class="h4 filter-headline bp-m"
                                    aria-expanded="{openTypeFilter}"
                                    on:click="{() => (openTypeFilter = !openTypeFilter)}">Type</button
                                >
                                <div class="option-grid gap-s" class:open="{openTypeFilter}">
                                    {#each product.lines as lineIdent}
                                        <div class="checkbox-logo">
                                            <figure>
                                                <img src="/assets/img/{lineIdent}-logo.svg" alt="{lineIdent}" />
                                            </figure>
                                        </div>
                                    {/each}
                                </div>
                            </div>
                        {/if}
                        {#if product.links?.length}
                            <nav class="product-nav col gap-m cart-only">
                                <LinkList links="{product.links}" />
                            </nav>
                        {/if}
                        <div class="hidden-md-up">
                            <div class="col-12 filters-wrapper">
                                <button
                                    class="h4 filter-headline tp-s bp-s"
                                    aria-expanded="{openMore}"
                                    on:click="{() => (openMore = !openMore)}">More</button
                                >
                            </div>
                        </div>
                        {#if product.links?.length}
                            <nav class="toggle-menu col-12" class:open="{openMore}">
                                <LinkList links="{product.links}" />
                            </nav>
                        {/if}
                    </div>
                    <div class="col-md-9 vp-xl site-content">
                        <section class="back-button vp-l">
                            <a
                                href="/products"
                                class="cta btn-secondary btn-arrow-left"
                                on:click|preventDefault="{backToList}">Back to the list</a
                            >
                        </section>
                        <section class="product-info">
                            {#if product.lines?.length}
                                <div class="hidden-md-up col-auto filters-wrapper float-right">
                                    <div class="option-grid gap-s open">
                                        {#each product.lines as lineIdent}
                                            <div class="checkbox-logo">
                                                <figure>
                                                    <img src="/assets/img/{lineIdent}-logo.svg" alt="{lineIdent}" />
                                                </figure>
                                            </div>
                                        {/each}
                                    </div>
                                </div>
                            {/if}
                            <h2>{product.title}</h2>
                            <div class="text vp-l">
                                {@html product.text}
                            </div>
                        </section>
                        {#if product.features?.length}
                            <section class="product-stats">
                                <div class="grid">
                                    {#each product.features as feature}
                                        <div class="col-lg-4 col-md-6 col-12">
                                            <h4 class="dark-gray-bg">{feature.name}</h4>
                                            <div class="description">{@html feature.value}</div>
                                        </div>
                                    {/each}
                                </div>
                            </section>
                        {/if}
                        {#if product.externalData?.isAvailable}
                            <!-- position 200px above -->
                            <div style="position:relative;">
                                <div
                                    bind:this="{configuratorScrollTargetContainer}"
                                    style="position: absolute; top: -200px; "
                                ></div>
                            </div>
                            <section class="product-configurator mt-xl">
                                {#if product.externalData?.bomItems?.length || product.externalData?.customAttributes?.length}
                                    <h3>Configure your product</h3>
                                {/if}
                                <div class="grid mt-l">
                                    <ProductConfigurator {product} bind:productConfig bind:bomItemConfigs />
                                </div>
                                <div class="grid button-wrap mt-l">
                                    {#if product.externalData?.bomItems?.length}
                                        <div class="col-sm-3"></div>
                                    {/if}
                                    <div class="col-auto">
                                        <button
                                            disabled="{(bomItemConfigs?.length &&
                                                !!bomItemConfigs.filter((c) => !c.valid).length) ||
                                                productConfig?.valid === false}"
                                            class="cta btn-primary"
                                            on:click="{() => {
                                                add2Cart(
                                                    product,
                                                    1,
                                                    productConfig,
                                                    product.externalData.bomItems?.length ? bomItemConfigs : undefined
                                                )

                                                // if siid is given, start with new configuration
                                                if (bomItemConfigs?.length) {
                                                    bomItemConfigs = bomItemConfigs.map((b) => {
                                                        /** @type {ArticleConfig} */
                                                        const _b = {
                                                            article: b.article,
                                                            customAttributes: cloneDeep(b.customAttributes),
                                                            valid: true,
                                                        }

                                                        // reset sportidentId
                                                        if (_b.customAttributes?.sportidentId) {
                                                            _b.customAttributes.sportidentId = {
                                                                value: '',
                                                                valid: true,
                                                            }
                                                        }

                                                        return _b
                                                    })
                                                }
                                            }}">Add to cart</button
                                        >
                                    </div>
                                </div>
                            </section>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </section>
{:else}
    <NotFound />
{/if}

<style>
    button:disabled {
        opacity: 0.2;
    }
</style>
